/**
 * @module @liquidefy/code-editor/terminal/guided/palette
 * @version 1.0.0
 * @license LQD Research LLC
 * @rust palette.rs — false; 4/2/26; sm
 *
 * Command palette and natural language bar — searchable command index
 * with fuzzy matching and NL-to-command translation.
 * All functions are pure. No DOM, no side effects.
 */

// ═══ §1 TYPES ═══

export type PaletteCategory =
  | 'file-management'
  | 'git'
  | 'navigation'
  | 'search'
  | 'process'
  | 'network'
  | 'package'
  | 'docker'
  | 'system'
  | 'build'
  | 'test'
  | 'deploy'
  | 'custom';

export interface PaletteEntry {
  readonly id: string;
  readonly command: string;
  readonly label: string;
  readonly description: string;
  readonly category: PaletteCategory;
  readonly keywords: string[];
  readonly riskLevel: 'safe' | 'moderate' | 'dangerous';
  readonly frequency: number;
  readonly lastUsed: number;
  readonly isCustom: boolean;
}

export interface PaletteState {
  readonly isOpen: boolean;
  readonly query: string;
  readonly entries: PaletteEntry[];
  readonly filteredEntries: PaletteEntry[];
  readonly selectedIndex: number;
  readonly recentCommands: PaletteEntry[];
  readonly maxRecent: number;
}

export interface NLTranslation {
  readonly input: string;
  readonly command: string;
  readonly confidence: number;
  readonly alternatives: string[];
  readonly explanation: string;
}

export interface PaletteSummary {
  readonly totalEntries: number;
  readonly customEntries: number;
  readonly categories: Record<PaletteCategory, number>;
}

// ═══ §2 DEFAULT ENTRIES ═══

interface EntryDef {
  readonly command: string;
  readonly label: string;
  readonly description: string;
  readonly category: PaletteCategory;
  readonly keywords: string[];
  readonly risk: 'safe' | 'moderate' | 'dangerous';
}

const DEFAULT_ENTRIES: EntryDef[] = [
  // --- File management ---
  { command: 'ls -la', label: 'List all files', description: 'Show all files including hidden, with details', category: 'file-management', keywords: ['list', 'files', 'directory', 'show', 'ls'], risk: 'safe' },
  { command: 'mkdir -p', label: 'Create folder', description: 'Create a new directory (with parents)', category: 'file-management', keywords: ['create', 'folder', 'directory', 'mkdir', 'new'], risk: 'safe' },
  { command: 'touch', label: 'Create empty file', description: 'Create a new empty file', category: 'file-management', keywords: ['create', 'file', 'new', 'touch', 'empty'], risk: 'safe' },
  { command: 'cp -r', label: 'Copy file or folder', description: 'Copy files or directories', category: 'file-management', keywords: ['copy', 'duplicate', 'cp'], risk: 'safe' },
  { command: 'mv', label: 'Move or rename', description: 'Move or rename files and directories', category: 'file-management', keywords: ['move', 'rename', 'mv'], risk: 'moderate' },
  { command: 'rm -i', label: 'Delete file (interactive)', description: 'Delete files with confirmation prompt', category: 'file-management', keywords: ['delete', 'remove', 'rm'], risk: 'moderate' },
  { command: 'rm -rf', label: 'Force delete', description: 'Recursively delete without confirmation', category: 'file-management', keywords: ['delete', 'remove', 'force', 'recursive'], risk: 'dangerous' },
  { command: 'cat', label: 'Show file contents', description: 'Display the entire contents of a file', category: 'file-management', keywords: ['show', 'read', 'display', 'cat', 'view', 'contents'], risk: 'safe' },
  { command: 'head -n 20', label: 'Show first lines', description: 'Display the first 20 lines of a file', category: 'file-management', keywords: ['head', 'first', 'top', 'beginning'], risk: 'safe' },
  { command: 'tail -n 20', label: 'Show last lines', description: 'Display the last 20 lines of a file', category: 'file-management', keywords: ['tail', 'last', 'bottom', 'end'], risk: 'safe' },
  { command: 'tail -f', label: 'Follow log file', description: 'Watch a file for new lines in real time', category: 'file-management', keywords: ['follow', 'watch', 'log', 'live', 'stream'], risk: 'safe' },
  { command: 'chmod', label: 'Change permissions', description: 'Change file or directory permissions', category: 'file-management', keywords: ['permissions', 'chmod', 'access', 'rights'], risk: 'moderate' },
  { command: 'tar -czf archive.tar.gz', label: 'Create archive', description: 'Compress files into a tar.gz archive', category: 'file-management', keywords: ['compress', 'zip', 'archive', 'tar', 'gzip'], risk: 'safe' },
  { command: 'tar -xzf', label: 'Extract archive', description: 'Extract a tar.gz archive', category: 'file-management', keywords: ['extract', 'unzip', 'untar', 'decompress'], risk: 'safe' },
  // --- Git ---
  { command: 'git status', label: 'Check status', description: 'Show working tree status', category: 'git', keywords: ['status', 'changes', 'modified'], risk: 'safe' },
  { command: 'git add .', label: 'Stage all changes', description: 'Add all modified files to staging', category: 'git', keywords: ['add', 'stage', 'all'], risk: 'safe' },
  { command: 'git commit -m ""', label: 'Commit changes', description: 'Create a commit with a message', category: 'git', keywords: ['commit', 'save', 'snapshot'], risk: 'safe' },
  { command: 'git push', label: 'Push to remote', description: 'Push commits to the remote repository', category: 'git', keywords: ['push', 'upload', 'remote', 'sync'], risk: 'safe' },
  { command: 'git pull', label: 'Pull from remote', description: 'Fetch and merge remote changes', category: 'git', keywords: ['pull', 'download', 'fetch', 'sync', 'update'], risk: 'safe' },
  { command: 'git log --oneline -20', label: 'View commit history', description: 'Show recent commit messages', category: 'git', keywords: ['log', 'history', 'commits', 'recent'], risk: 'safe' },
  { command: 'git diff', label: 'Show changes', description: 'Show unstaged changes', category: 'git', keywords: ['diff', 'changes', 'compare'], risk: 'safe' },
  { command: 'git branch', label: 'List branches', description: 'Show all local branches', category: 'git', keywords: ['branch', 'branches', 'list'], risk: 'safe' },
  { command: 'git checkout -b', label: 'Create branch', description: 'Create and switch to a new branch', category: 'git', keywords: ['branch', 'create', 'new', 'checkout'], risk: 'safe' },
  { command: 'git merge', label: 'Merge branch', description: 'Merge a branch into current branch', category: 'git', keywords: ['merge', 'combine', 'integrate'], risk: 'moderate' },
  { command: 'git stash', label: 'Stash changes', description: 'Temporarily save uncommitted changes', category: 'git', keywords: ['stash', 'save', 'temporary', 'shelve'], risk: 'safe' },
  { command: 'git stash pop', label: 'Restore stash', description: 'Restore previously stashed changes', category: 'git', keywords: ['stash', 'restore', 'pop', 'apply'], risk: 'safe' },
  { command: 'git reset --soft HEAD~1', label: 'Undo last commit (keep changes)', description: 'Undo the last commit but keep files staged', category: 'git', keywords: ['undo', 'reset', 'uncommit'], risk: 'moderate' },
  // --- Navigation ---
  { command: 'cd', label: 'Go to folder', description: 'Change to a specific directory', category: 'navigation', keywords: ['go', 'change', 'directory', 'cd', 'navigate'], risk: 'safe' },
  { command: 'cd ~', label: 'Go home', description: 'Change to home directory', category: 'navigation', keywords: ['home', 'cd', 'tilde'], risk: 'safe' },
  { command: 'cd ..', label: 'Go up one level', description: 'Move to parent directory', category: 'navigation', keywords: ['up', 'parent', 'back'], risk: 'safe' },
  { command: 'pwd', label: 'Show current location', description: 'Print the current working directory', category: 'navigation', keywords: ['where', 'pwd', 'location', 'current', 'path'], risk: 'safe' },
  { command: 'tree -L 2', label: 'Show folder structure', description: 'Display directory tree (2 levels deep)', category: 'navigation', keywords: ['tree', 'structure', 'folders', 'hierarchy'], risk: 'safe' },
  // --- Search ---
  { command: 'grep -rn "" .', label: 'Search in files', description: 'Search for text across all files recursively', category: 'search', keywords: ['search', 'find', 'grep', 'text', 'pattern'], risk: 'safe' },
  { command: 'find . -name ""', label: 'Find file by name', description: 'Search for files matching a pattern', category: 'search', keywords: ['find', 'file', 'name', 'locate'], risk: 'safe' },
  { command: 'find . -size +100M -type f', label: 'Find large files', description: 'Find files larger than 100MB', category: 'search', keywords: ['large', 'big', 'size', 'space', 'files'], risk: 'safe' },
  { command: 'which', label: 'Find command location', description: 'Show the path of an executable', category: 'search', keywords: ['which', 'where', 'location', 'path', 'command'], risk: 'safe' },
  // --- Process ---
  { command: 'ps aux', label: 'Show running processes', description: 'List all running processes with details', category: 'process', keywords: ['processes', 'running', 'ps', 'list'], risk: 'safe' },
  { command: 'kill', label: 'Kill a process', description: 'Terminate a process by PID', category: 'process', keywords: ['kill', 'stop', 'terminate', 'process'], risk: 'moderate' },
  { command: 'lsof -i', label: 'Check port usage', description: 'Show which processes are using network ports', category: 'process', keywords: ['port', 'lsof', 'network', 'listen', 'using'], risk: 'safe' },
  { command: 'top', label: 'System monitor', description: 'Interactive process and resource monitor', category: 'process', keywords: ['monitor', 'top', 'cpu', 'memory', 'resources'], risk: 'safe' },
  { command: 'jobs', label: 'Background jobs', description: 'List background jobs in current shell', category: 'process', keywords: ['jobs', 'background', 'running'], risk: 'safe' },
  // --- Network ---
  { command: 'curl', label: 'Make HTTP request', description: 'Send an HTTP request to a URL', category: 'network', keywords: ['curl', 'http', 'request', 'api', 'get', 'post'], risk: 'safe' },
  { command: 'ping', label: 'Ping a host', description: 'Test connectivity to a remote host', category: 'network', keywords: ['ping', 'connectivity', 'network', 'test'], risk: 'safe' },
  { command: 'ssh', label: 'SSH connect', description: 'Connect to a remote server via SSH', category: 'network', keywords: ['ssh', 'remote', 'connect', 'server', 'login'], risk: 'safe' },
  { command: 'wget', label: 'Download file', description: 'Download a file from a URL', category: 'network', keywords: ['download', 'wget', 'fetch', 'file'], risk: 'safe' },
  { command: 'ifconfig', label: 'Network interfaces', description: 'Show network interface configuration', category: 'network', keywords: ['network', 'ip', 'interface', 'address'], risk: 'safe' },
  // --- Package ---
  { command: 'npm install', label: 'Install npm packages', description: 'Install project dependencies', category: 'package', keywords: ['npm', 'install', 'dependencies', 'node'], risk: 'safe' },
  { command: 'npm run', label: 'Run npm script', description: 'Execute a script from package.json', category: 'package', keywords: ['npm', 'run', 'script', 'execute'], risk: 'safe' },
  { command: 'npm update', label: 'Update npm packages', description: 'Update installed packages', category: 'package', keywords: ['npm', 'update', 'upgrade'], risk: 'safe' },
  { command: 'pnpm install', label: 'Install pnpm packages', description: 'Install dependencies with pnpm', category: 'package', keywords: ['pnpm', 'install', 'dependencies'], risk: 'safe' },
  { command: 'pip install', label: 'Install Python package', description: 'Install a Python package', category: 'package', keywords: ['pip', 'python', 'install', 'package'], risk: 'safe' },
  // --- Docker ---
  { command: 'docker ps', label: 'List containers', description: 'Show running Docker containers', category: 'docker', keywords: ['docker', 'containers', 'running', 'list', 'ps'], risk: 'safe' },
  { command: 'docker build -t', label: 'Build image', description: 'Build a Docker image from Dockerfile', category: 'docker', keywords: ['docker', 'build', 'image', 'dockerfile'], risk: 'safe' },
  { command: 'docker run', label: 'Run container', description: 'Create and start a new container', category: 'docker', keywords: ['docker', 'run', 'start', 'container'], risk: 'safe' },
  { command: 'docker logs', label: 'Container logs', description: 'View logs from a running container', category: 'docker', keywords: ['docker', 'logs', 'output', 'container'], risk: 'safe' },
  { command: 'docker stop', label: 'Stop container', description: 'Stop a running container', category: 'docker', keywords: ['docker', 'stop', 'halt', 'container'], risk: 'safe' },
  { command: 'docker-compose up -d', label: 'Start services', description: 'Start Docker Compose services in background', category: 'docker', keywords: ['compose', 'up', 'start', 'services'], risk: 'safe' },
  { command: 'docker-compose down', label: 'Stop services', description: 'Stop and remove Docker Compose services', category: 'docker', keywords: ['compose', 'down', 'stop', 'services'], risk: 'moderate' },
  // --- System ---
  { command: 'df -h', label: 'Check disk space', description: 'Show filesystem disk space usage', category: 'system', keywords: ['disk', 'space', 'storage', 'df', 'free'], risk: 'safe' },
  { command: 'du -sh *', label: 'Folder sizes', description: 'Show size of each item in current directory', category: 'system', keywords: ['size', 'disk', 'usage', 'du', 'folder', 'space'], risk: 'safe' },
  { command: 'free -h', label: 'Check memory', description: 'Show available and used memory', category: 'system', keywords: ['memory', 'ram', 'free', 'available'], risk: 'safe' },
  { command: 'uname -a', label: 'System info', description: 'Show system/kernel information', category: 'system', keywords: ['system', 'info', 'kernel', 'os', 'version'], risk: 'safe' },
  { command: 'uptime', label: 'System uptime', description: 'Show how long the system has been running', category: 'system', keywords: ['uptime', 'running', 'since', 'time'], risk: 'safe' },
  { command: 'whoami', label: 'Current user', description: 'Show the current logged-in user', category: 'system', keywords: ['user', 'whoami', 'name', 'current'], risk: 'safe' },
  { command: 'env', label: 'Environment variables', description: 'List all environment variables', category: 'system', keywords: ['env', 'environment', 'variables', 'config'], risk: 'safe' },
  { command: 'date', label: 'Current date/time', description: 'Show the current date and time', category: 'system', keywords: ['date', 'time', 'now', 'clock'], risk: 'safe' },
  // --- Build ---
  { command: 'npm run build', label: 'Build project (npm)', description: 'Run the build script', category: 'build', keywords: ['build', 'compile', 'bundle', 'npm'], risk: 'safe' },
  { command: 'make', label: 'Run Makefile', description: 'Execute the default Makefile target', category: 'build', keywords: ['make', 'build', 'compile'], risk: 'safe' },
  { command: 'cargo build', label: 'Build Rust project', description: 'Compile a Rust project', category: 'build', keywords: ['cargo', 'rust', 'build', 'compile'], risk: 'safe' },
  // --- Test ---
  { command: 'npm test', label: 'Run tests (npm)', description: 'Execute the test suite', category: 'test', keywords: ['test', 'tests', 'npm', 'jest', 'mocha'], risk: 'safe' },
  { command: 'npm run test -- --watch', label: 'Watch tests', description: 'Run tests in watch mode', category: 'test', keywords: ['test', 'watch', 'live', 'rerun'], risk: 'safe' },
  { command: 'cargo test', label: 'Run Rust tests', description: 'Execute Rust test suite', category: 'test', keywords: ['cargo', 'rust', 'test'], risk: 'safe' },
  // --- Deploy ---
  { command: 'npm publish', label: 'Publish npm package', description: 'Publish package to npm registry', category: 'deploy', keywords: ['publish', 'npm', 'release', 'registry'], risk: 'moderate' },
  { command: 'docker push', label: 'Push Docker image', description: 'Push a Docker image to a registry', category: 'deploy', keywords: ['docker', 'push', 'registry', 'deploy', 'image'], risk: 'moderate' },
  { command: 'rsync -avz', label: 'Sync files to server', description: 'Synchronize files to a remote server', category: 'deploy', keywords: ['rsync', 'sync', 'deploy', 'upload', 'server'], risk: 'moderate' },
];

// ═══ §3 NL PATTERNS ═══

interface NLPattern {
  readonly patterns: RegExp[];
  readonly command: string;
  readonly explanation: string;
  readonly alternatives: string[];
}

const NL_PATTERNS: NLPattern[] = [
  // File operations
  { patterns: [/show\s*(me\s+)?(?:all\s+)?files/i, /list\s*(?:all\s+)?files/i, /what\s*(?:files|is)\s*(?:are\s+)?(?:in\s+)?(?:here|this\s+(?:folder|dir))/i],
    command: 'ls -la', explanation: 'Lists all files including hidden ones with details', alternatives: ['ls', 'ls -lh', 'tree'] },
  { patterns: [/create\s*(?:a\s+)?(?:new\s+)?(?:folder|directory|dir)/i, /make\s*(?:a\s+)?(?:new\s+)?(?:folder|directory|dir)/i, /new\s+folder/i],
    command: 'mkdir -p', explanation: 'Creates a new directory, including parent directories', alternatives: ['mkdir'] },
  { patterns: [/create\s*(?:a\s+)?(?:new\s+)?(?:file|empty\s+file)/i, /make\s*(?:a\s+)?(?:new\s+)?file/i, /new\s+file/i],
    command: 'touch', explanation: 'Creates a new empty file', alternatives: [] },
  { patterns: [/delete\s*(?:this\s+)?(?:file|files)/i, /remove\s*(?:this\s+)?(?:file|files)/i, /get\s+rid\s+of/i],
    command: 'rm -i', explanation: 'Deletes files with interactive confirmation', alternatives: ['rm', 'trash'] },
  { patterns: [/(?:show|view|read|display|open)\s*(?:the\s+)?(?:contents?\s+(?:of\s+)?)?(?:file|this)/i, /what(?:'s| is)\s+in\s+(?:this\s+)?file/i],
    command: 'cat', explanation: 'Displays the full contents of a file', alternatives: ['less', 'head', 'tail'] },
  { patterns: [/copy\s*(?:this\s+)?(?:file|folder|directory)/i, /duplicate/i],
    command: 'cp -r', explanation: 'Copies files or directories', alternatives: ['cp'] },
  { patterns: [/move\s*(?:this\s+)?(?:file|folder)/i, /rename\s*(?:this\s+)?(?:file|folder)/i],
    command: 'mv', explanation: 'Moves or renames files and directories', alternatives: [] },
  { patterns: [/find\s+(?:large|big)\s+files/i, /(?:large|big)\s+files/i, /what(?:'s| is)\s+taking\s+(?:up\s+)?space/i],
    command: 'find . -size +100M -type f', explanation: 'Finds files larger than 100MB', alternatives: ['du -sh * | sort -rh | head -20'] },
  { patterns: [/compress|archive|zip\s+(?:up|these|this)/i, /tar\s+(?:up|these)/i, /create\s+(?:an?\s+)?archive/i],
    command: 'tar -czf archive.tar.gz', explanation: 'Creates a gzipped tar archive', alternatives: ['zip -r'] },
  { patterns: [/extract|unzip|untar|decompress/i],
    command: 'tar -xzf', explanation: 'Extracts a tar.gz archive', alternatives: ['unzip'] },

  // Git
  { patterns: [/(?:save|commit)\s*(?:my\s+)?(?:changes|work|progress)/i, /check\s*(?:in|point)/i],
    command: 'git add . && git commit -m ""', explanation: 'Stages all changes and creates a commit', alternatives: ['git commit -am ""'] },
  { patterns: [/push\s*(?:to\s+)?(?:remote|server|github|origin)/i, /upload\s+(?:my\s+)?(?:changes|code|commits)/i, /sync\s+(?:to|with)\s+remote/i],
    command: 'git push', explanation: 'Pushes local commits to the remote repository', alternatives: ['git push origin main'] },
  { patterns: [/pull\s+(?:latest|changes|updates)/i, /get\s+(?:latest|updates)/i, /update\s+(?:from\s+)?(?:remote|origin)/i, /sync\s+(?:from|with)\s+remote/i],
    command: 'git pull', explanation: 'Fetches and merges changes from the remote', alternatives: ['git fetch && git merge'] },
  { patterns: [/(?:check|show|what(?:'s| is))\s*(?:the\s+)?(?:git\s+)?status/i, /what\s+(?:changed|did\s+i\s+change)/i, /uncommitted\s+changes/i],
    command: 'git status', explanation: 'Shows which files have been modified, staged, or are untracked', alternatives: ['git diff --stat'] },
  { patterns: [/(?:create|make|new)\s*(?:a\s+)?(?:new\s+)?branch/i],
    command: 'git checkout -b', explanation: 'Creates and switches to a new branch', alternatives: ['git switch -c'] },
  { patterns: [/merge\s*(?:this\s+)?branch/i, /combine\s+branches/i],
    command: 'git merge', explanation: 'Merges another branch into the current one', alternatives: ['git rebase'] },
  { patterns: [/(?:show|view)\s*(?:commit\s+)?(?:history|log)/i, /recent\s+commits/i],
    command: 'git log --oneline -20', explanation: 'Shows the last 20 commits in compact format', alternatives: ['git log --graph --oneline'] },
  { patterns: [/(?:show|view)\s*(?:the\s+)?(?:diff|changes|modifications)/i, /what\s+(?:did\s+i\s+)?change/i],
    command: 'git diff', explanation: 'Shows unstaged changes in the working directory', alternatives: ['git diff --staged'] },
  { patterns: [/undo\s+(?:last\s+)?commit/i, /uncommit/i],
    command: 'git reset --soft HEAD~1', explanation: 'Undoes the last commit but keeps changes staged', alternatives: ['git revert HEAD'] },
  { patterns: [/stash\s*(?:my\s+)?(?:changes|work)/i, /save\s+(?:changes\s+)?(?:for\s+)?later/i],
    command: 'git stash', explanation: 'Temporarily saves uncommitted changes', alternatives: ['git stash push -m "description"'] },

  // Navigation
  { patterns: [/where\s+am\s+i/i, /(?:show|print|what(?:'s| is))\s*(?:the\s+)?(?:current\s+)?(?:directory|location|path|pwd)/i],
    command: 'pwd', explanation: 'Prints the current working directory', alternatives: [] },
  { patterns: [/go\s+(?:to\s+)?home/i, /home\s+directory/i, /cd\s+home/i],
    command: 'cd ~', explanation: 'Changes to the home directory', alternatives: ['cd $HOME'] },
  { patterns: [/go\s+(?:up|back)\s*(?:one\s+level)?/i, /parent\s+(?:directory|folder)/i],
    command: 'cd ..', explanation: 'Moves to the parent directory', alternatives: [] },
  { patterns: [/(?:show|display)\s*(?:the\s+)?(?:folder|directory)\s*(?:structure|tree)/i, /tree\s+view/i],
    command: 'tree -L 2', explanation: 'Shows directory structure two levels deep', alternatives: ['tree -L 3', 'find . -type d'] },

  // Search
  { patterns: [/search\s+(?:for\s+)?(?:text|string|pattern)\s+(?:in\s+)?(?:files|code)/i, /find\s+(?:text|string)\s+in\s+files/i, /grep\s+for/i],
    command: 'grep -rn "" .', explanation: 'Searches for a pattern across all files recursively', alternatives: ['rg ""', 'ag ""'] },
  { patterns: [/find\s+(?:a\s+)?file\s+(?:named|called)/i, /locate\s+(?:a\s+)?file/i, /where\s+is\s+(?:the\s+)?file/i],
    command: 'find . -name ""', explanation: 'Searches for files matching a name pattern', alternatives: ['fd ""', 'locate'] },

  // Process
  { patterns: [/(?:show|list)\s*(?:all\s+)?(?:running\s+)?processes/i, /what(?:'s| is)\s+running/i],
    command: 'ps aux', explanation: 'Lists all running processes with CPU/memory usage', alternatives: ['top', 'htop'] },
  { patterns: [/kill\s+(?:a\s+)?process/i, /stop\s+(?:a\s+)?process/i, /terminate\s+(?:a\s+)?process/i],
    command: 'kill', explanation: 'Sends a termination signal to a process by PID', alternatives: ['kill -9', 'killall'] },
  { patterns: [/what(?:'s| is)\s+(?:using|on)\s+port\s*(\d*)/i, /(?:check|show)\s+port\s*(?:usage|\d*)/i, /port\s+(\d+)\s+(?:in\s+)?use/i],
    command: 'lsof -i :3000', explanation: 'Shows which process is using the specified port', alternatives: ['netstat -tlnp', 'ss -tlnp'] },
  { patterns: [/free\s+(?:up\s+)?port\s*(\d*)/i, /kill\s+(?:whatever(?:'s| is)\s+)?(?:on|using)\s+port/i],
    command: 'lsof -ti :3000 | xargs kill', explanation: 'Kills the process using the specified port', alternatives: ['fuser -k 3000/tcp'] },

  // System
  { patterns: [/(?:check|show|how\s+much)\s*(?:free\s+)?disk\s+space/i, /(?:disk|storage)\s+(?:usage|left|remaining|free)/i, /how\s+much\s+space\s+(?:left|do\s+i\s+have|remaining)/i],
    command: 'df -h', explanation: 'Shows disk space usage for all filesystems in human-readable format', alternatives: ['du -sh /'] },
  { patterns: [/(?:check|show|how\s+much)\s*(?:free\s+)?(?:memory|ram)/i, /memory\s+(?:usage|available|left)/i],
    command: 'free -h', explanation: 'Shows available and used memory in human-readable format', alternatives: ['top', 'vm_stat'] },
  { patterns: [/system\s+(?:info|information)/i, /what\s+(?:os|system)\s+(?:am\s+i|is\s+this)/i, /kernel\s+version/i],
    command: 'uname -a', explanation: 'Shows detailed system and kernel information', alternatives: ['lsb_release -a', 'sw_vers'] },
  { patterns: [/(?:current\s+)?(?:date|time)\s*(?:and\s+(?:date|time))?/i, /what\s+(?:time|day)\s+is\s+it/i],
    command: 'date', explanation: 'Shows the current date and time', alternatives: ['date +%Y-%m-%d'] },
  { patterns: [/(?:who\s+am\s+i|current\s+user|my\s+username)/i],
    command: 'whoami', explanation: 'Shows the current logged-in username', alternatives: ['id'] },
  { patterns: [/(?:show|list|view)\s*(?:environment|env)\s*(?:variables|vars)/i],
    command: 'env', explanation: 'Lists all environment variables', alternatives: ['printenv', 'export'] },

  // Package
  { patterns: [/install\s+(?:npm\s+)?(?:packages?|dependencies)/i, /npm\s+install/i],
    command: 'npm install', explanation: 'Installs project dependencies from package.json', alternatives: ['pnpm install', 'yarn install'] },
  { patterns: [/update\s+(?:all\s+)?(?:packages?|dependencies)/i],
    command: 'npm update', explanation: 'Updates installed packages to latest compatible versions', alternatives: ['pnpm update', 'yarn upgrade'] },

  // Docker
  { patterns: [/(?:list|show)\s*(?:running\s+)?(?:docker\s+)?containers/i],
    command: 'docker ps', explanation: 'Lists running Docker containers', alternatives: ['docker ps -a'] },
  { patterns: [/(?:build|create)\s*(?:a\s+)?(?:docker\s+)?image/i],
    command: 'docker build -t', explanation: 'Builds a Docker image from a Dockerfile', alternatives: ['docker-compose build'] },
  { patterns: [/start\s+(?:docker\s+)?(?:compose\s+)?services/i, /docker.?compose\s+up/i],
    command: 'docker-compose up -d', explanation: 'Starts Docker Compose services in the background', alternatives: ['docker-compose up'] },

  // Network
  { patterns: [/(?:make|send)\s*(?:an?\s+)?(?:http|api)\s+(?:request|call)/i, /curl\s+(?:to|a)/i, /hit\s+(?:the\s+)?(?:api|endpoint)/i],
    command: 'curl', explanation: 'Sends an HTTP request to a URL', alternatives: ['wget', 'httpie'] },
  { patterns: [/(?:test|check)\s*(?:network\s+)?connectivity/i, /ping\s+(?:a\s+)?(?:host|server)/i, /is\s+(?:the\s+)?(?:server|site)\s+(?:up|running|alive)/i],
    command: 'ping', explanation: 'Sends ICMP packets to test connectivity', alternatives: ['curl -I', 'traceroute'] },
  { patterns: [/connect\s+(?:to\s+)?(?:remote\s+)?server/i, /ssh\s+(?:into|to)/i, /remote\s+login/i],
    command: 'ssh', explanation: 'Opens a secure shell connection to a remote server', alternatives: [] },
  { patterns: [/download\s+(?:a\s+)?file/i],
    command: 'wget', explanation: 'Downloads a file from a URL', alternatives: ['curl -O'] },

  // Build/Test
  { patterns: [/build\s+(?:the\s+)?(?:project|app|code)/i, /compile/i],
    command: 'npm run build', explanation: 'Runs the project build script', alternatives: ['make', 'cargo build'] },
  { patterns: [/run\s+(?:the\s+)?tests?/i, /test\s+(?:the\s+)?(?:code|project)/i],
    command: 'npm test', explanation: 'Runs the project test suite', alternatives: ['npm run test', 'cargo test'] },
  { patterns: [/(?:clean|clear)\s+(?:the\s+)?(?:build|cache|dist)/i],
    command: 'rm -rf dist/ build/ .cache/', explanation: 'Removes common build output directories', alternatives: ['npm run clean'] },
];

// ═══ §4 FUZZY MATCHING ═══

export function fuzzyMatch(query: string, target: string): number {
  if (!query || !target) return 0;

  const q = query.toLowerCase();
  const t = target.toLowerCase();

  // Exact match
  if (q === t) return 1.0;

  // Prefix match
  if (t.startsWith(q)) return 0.9;

  // Substring match — bonus for earlier position
  const subIdx = t.indexOf(q);
  if (subIdx !== -1) return 0.7 + (0.1 * (1 - subIdx / t.length));

  // Acronym match: "gco" matches "git checkout"
  if (matchesAcronym(q, t)) return 0.6;

  // Subsequence matching with gap penalty
  let qi = 0;
  let consecutive = 0;
  let maxConsecutive = 0;
  let prevMatchIdx = -2;

  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) {
      consecutive = (ti === prevMatchIdx + 1) ? consecutive + 1 : 1;
      maxConsecutive = Math.max(maxConsecutive, consecutive);
      prevMatchIdx = ti;
      qi++;
    }
  }

  if (qi < q.length) return 0; // Not all chars matched

  const coverage = q.length / t.length;
  const consecutiveBonus = maxConsecutive / q.length;
  return Math.min(0.5, 0.2 + coverage * 0.15 + consecutiveBonus * 0.15);
}

function matchesAcronym(query: string, target: string): boolean {
  const words = target.split(/[\s\-_]+/);
  if (words.length < 2 || query.length > words.length) return false;

  let qi = 0;
  for (const word of words) {
    if (qi < query.length && word.length > 0 && word[0] === query[qi]) {
      qi++;
    }
  }
  return qi === query.length;
}

// ═══ §5 STATE MANAGEMENT ═══

export function buildDefaultEntries(): PaletteEntry[] {
  return DEFAULT_ENTRIES.map((e, i) => ({
    id: `builtin-${i}`,
    command: e.command,
    label: e.label,
    description: e.description,
    category: e.category,
    keywords: e.keywords,
    riskLevel: e.risk,
    frequency: 0,
    lastUsed: 0,
    isCustom: false,
  }));
}

export function createPaletteState(): PaletteState {
  const entries = buildDefaultEntries();
  return {
    isOpen: false,
    query: '',
    entries,
    filteredEntries: entries,
    selectedIndex: 0,
    recentCommands: [],
    maxRecent: 20,
  };
}

export function openPalette(state: PaletteState): PaletteState {
  return { ...state, isOpen: true, query: '', filteredEntries: state.entries, selectedIndex: 0 };
}

export function closePalette(state: PaletteState): PaletteState {
  return { ...state, isOpen: false, query: '', selectedIndex: 0 };
}

export function updateQuery(state: PaletteState, query: string): PaletteState {
  if (!query.trim()) {
    return { ...state, query, filteredEntries: state.entries, selectedIndex: 0 };
  }

  const scored = state.entries.map(entry => {
    const labelScore = fuzzyMatch(query, entry.label);
    const descScore = fuzzyMatch(query, entry.description) * 0.8;
    const cmdScore = fuzzyMatch(query, entry.command) * 0.7;
    const keywordScore = Math.max(0, ...entry.keywords.map(k => fuzzyMatch(query, k))) * 0.9;
    const bestScore = Math.max(labelScore, descScore, cmdScore, keywordScore);
    // Boost by frequency
    const freqBoost = Math.min(entry.frequency * 0.01, 0.1);
    return { entry, score: bestScore + freqBoost };
  });

  const filtered = scored
    .filter(s => s.score > 0.1)
    .sort((a, b) => b.score - a.score)
    .map(s => s.entry);

  return { ...state, query, filteredEntries: filtered, selectedIndex: 0 };
}

export function selectNext(state: PaletteState): PaletteState {
  if (state.filteredEntries.length === 0) return state;
  const next = (state.selectedIndex + 1) % state.filteredEntries.length;
  return { ...state, selectedIndex: next };
}

export function selectPrevious(state: PaletteState): PaletteState {
  if (state.filteredEntries.length === 0) return state;
  const prev = state.selectedIndex <= 0 ? state.filteredEntries.length - 1 : state.selectedIndex - 1;
  return { ...state, selectedIndex: prev };
}

export function getSelectedEntry(state: PaletteState): PaletteEntry | null {
  if (state.selectedIndex < 0 || state.selectedIndex >= state.filteredEntries.length) return null;
  return state.filteredEntries[state.selectedIndex] ?? null;
}

export function executeSelected(state: PaletteState): { state: PaletteState; command: string | null } {
  const entry = getSelectedEntry(state);
  if (!entry) return { state, command: null };

  const now = Date.now();
  const updated = recordUsage(state, entry.id);
  const recent = [entry, ...updated.recentCommands.filter(r => r.id !== entry.id)].slice(0, updated.maxRecent);
  const closed = closePalette({ ...updated, recentCommands: recent });
  return { state: closed, command: entry.command };
}

export function addCustomEntry(
  state: PaletteState,
  entry: Omit<PaletteEntry, 'id' | 'frequency' | 'lastUsed' | 'isCustom'>,
): PaletteState {
  const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const newEntry: PaletteEntry = { ...entry, id, frequency: 0, lastUsed: 0, isCustom: true };
  const entries = [...state.entries, newEntry];
  return { ...state, entries, filteredEntries: state.query ? state.filteredEntries : entries };
}

export function removeEntry(state: PaletteState, id: string): PaletteState {
  const entries = state.entries.filter(e => e.id !== id);
  const filteredEntries = state.filteredEntries.filter(e => e.id !== id);
  const recentCommands = state.recentCommands.filter(e => e.id !== id);
  const selectedIndex = Math.min(state.selectedIndex, Math.max(0, filteredEntries.length - 1));
  return { ...state, entries, filteredEntries, recentCommands, selectedIndex };
}

export function recordUsage(state: PaletteState, entryId: string): PaletteState {
  const now = Date.now();
  const entries = state.entries.map(e =>
    e.id === entryId ? { ...e, frequency: e.frequency + 1, lastUsed: now } : e
  );
  const filteredEntries = state.filteredEntries.map(e =>
    e.id === entryId ? { ...e, frequency: e.frequency + 1, lastUsed: now } : e
  );
  return { ...state, entries, filteredEntries };
}

// ═══ §6 QUERIES ═══

export function getEntriesByCategory(state: PaletteState, category: PaletteCategory): PaletteEntry[] {
  return state.entries.filter(e => e.category === category);
}

export function getRecentEntries(state: PaletteState, count: number = 10): PaletteEntry[] {
  return state.recentCommands.slice(0, count);
}

export function getMostUsedEntries(state: PaletteState, count: number = 10): PaletteEntry[] {
  return [...state.entries].sort((a, b) => b.frequency - a.frequency).slice(0, count);
}

export function getPaletteSummary(state: PaletteState): PaletteSummary {
  const categories = {} as Record<PaletteCategory, number>;
  const allCats: PaletteCategory[] = [
    'file-management', 'git', 'navigation', 'search', 'process', 'network',
    'package', 'docker', 'system', 'build', 'test', 'deploy', 'custom',
  ];
  for (const c of allCats) categories[c] = 0;
  for (const e of state.entries) categories[e.category]++;

  return {
    totalEntries: state.entries.length,
    customEntries: state.entries.filter(e => e.isCustom).length,
    categories,
  };
}

// ═══ §7 NATURAL LANGUAGE TRANSLATION ═══

export function translateNaturalLanguage(input: string): NLTranslation {
  const trimmed = input.trim();
  if (!trimmed) {
    return { input: trimmed, command: '', confidence: 0, alternatives: [], explanation: 'No input provided' };
  }

  // Try pattern matching first
  for (const nlp of NL_PATTERNS) {
    for (const pattern of nlp.patterns) {
      const match = pattern.exec(trimmed);
      if (match) {
        // Extract port number if captured
        let command = nlp.command;
        if (match[1] && /^\d+$/.test(match[1])) {
          command = command.replace(/\d{4}/, match[1]);
        }
        return {
          input: trimmed,
          command,
          confidence: 0.85,
          alternatives: nlp.alternatives,
          explanation: nlp.explanation,
        };
      }
    }
  }

  // Fall back to fuzzy matching against default entries
  const entries = DEFAULT_ENTRIES;
  let bestScore = 0;
  let bestEntry: EntryDef | null = null;
  const otherMatches: string[] = [];

  for (const entry of entries) {
    const labelScore = fuzzyMatch(trimmed, entry.label);
    const descScore = fuzzyMatch(trimmed, entry.description) * 0.9;
    const keywordScore = Math.max(0, ...entry.keywords.map(k => fuzzyMatch(trimmed, k))) * 0.85;
    const score = Math.max(labelScore, descScore, keywordScore);

    if (score > bestScore) {
      if (bestEntry) otherMatches.push(bestEntry.command);
      bestScore = score;
      bestEntry = entry;
    } else if (score > 0.3) {
      otherMatches.push(entry.command);
    }
  }

  if (bestEntry && bestScore > 0.25) {
    return {
      input: trimmed,
      command: bestEntry.command,
      confidence: Math.min(bestScore, 0.7),
      alternatives: otherMatches.slice(0, 3),
      explanation: bestEntry.description,
    };
  }

  return {
    input: trimmed,
    command: '',
    confidence: 0,
    alternatives: [],
    explanation: 'Could not translate to a command. Try rephrasing or use the command palette to browse available commands.',
  };
}
